/**
 * API client for the Rocketships platform.
 * @spec SPEC:ENGINEERING:FRONTEND:API
 */
import { logger } from '@/services/logger'

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3000'

class ApiClient {
  private token: string | null = null

  setToken(token: string | null) {
    this.token = token
    if (token) {
      localStorage.setItem('auth_token', token)
    } else {
      localStorage.removeItem('auth_token')
    }
  }

  getToken(): string | null {
    if (!this.token) {
      this.token = localStorage.getItem('auth_token')
    }
    return this.token
  }

  private async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const url = `${API_BASE}/v1${path}`
    const headers: Record<string, string> = {}

    const token = this.getToken()
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    logger.debug(`api.${method.toLowerCase()} ${path}`)

    const init: RequestInit = { method, headers }
    if (body !== undefined) {
      headers['Content-Type'] = 'application/json'
      init.body = JSON.stringify(body)
    }

    const response = await fetch(url, init)

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: { message: response.statusText } }))
      const apiError = new Error(error.error?.message ?? 'Request failed') as Error & { status: number }
      apiError.status = response.status
      throw apiError
    }

    if (response.status === 204) return undefined as T
    const text = await response.text()
    if (!text) return undefined as T
    return JSON.parse(text) as T
  }

  get<T>(path: string) { return this.request<T>('GET', path) }
  post<T>(path: string, body?: unknown) { return this.request<T>('POST', path, body) }
  patch<T>(path: string, body?: unknown) { return this.request<T>('PATCH', path, body) }
  delete<T>(path: string) { return this.request<T>('DELETE', path) }

  /** Download a binary file as a blob and trigger browser download. */
  async download(path: string, filename: string): Promise<void> {
    const url = `${API_BASE}/v1${path}`
    const headers: Record<string, string> = {}
    const token = this.getToken()
    if (token) headers['Authorization'] = `Bearer ${token}`

    const response = await fetch(url, { headers })
    if (!response.ok) {
      throw new Error(`Download failed: ${response.statusText}`)
    }

    const blob = await response.blob()
    const blobUrl = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = blobUrl
    a.download = filename
    a.click()
    URL.revokeObjectURL(blobUrl)
  }
}

export const api = new ApiClient()
